/**
 * Tours service — catálogo, detalle y cotización (F1).
 *
 * Todas las llamadas pasan por las edge functions tours-catalog /
 * tours-experience / tours-quote, que corren con service_role y exponen el
 * precio final al guest. El host_net_cents nunca viaja al cliente: el servicio
 * recibe `guestPriceCents` ya derivado por el solver compartido.
 */
import { NativeModules, Platform } from 'react-native'
import * as Device from 'expo-device'
import { supabase } from '../supabase'
import { ServiceError } from '../errors/service-error'
import {
  androidEmulatorAssetHost,
  metroOriginFromScriptURL,
  rewriteAndroidEmulatorLoopback,
} from '../utils/android-emulator-loopback'
import {
  cardToCreatePaymentWire,
  chargeIntentFromCreatePayment,
  payerToCreatePaymentWire,
  type CheckoutPayer,
} from '../payments/charge-result'
import { optionalClientCheckoutOrigin } from '../payments/checkout-origin'
import { isIsolatedPreviewBuild } from '../runtime-build'
import { useToursCheckoutStore } from '../stores/tours-checkout.store'
import {
  peekPreviewSplit,
  previewCreateSplit,
  previewPaySplit,
  previewPayTour,
  previewSplitInfo,
} from '../tours/preview-payments'
import { resolvePreviewMoneyPath } from '../tours/preview-money-policy'
import type { CardPaymentData } from '../types/payment.types'
import type {
  CreateTourBookingInput,
  TourBookingStatus,
  TourBookingSummary,
  TourCatalogExperience,
  TourCustomQuoteInput,
  TourCustomQuoteResult,
  TourExperienceDetail,
  TourHost,
  TourHostBalance,
  TourHostBooking,
  TourHostDeparture,
  TourHostDepartureInput,
  TourHostDeparturesResult,
  TourHostExperience,
  TourHostProfileInput,
  TourHostUnit,
  TourHostUnitInput,
  TourGuardianShareInput,
  TourGuardianShareResult,
  TourGuardianSnapshot,
  TourPayInput,
  TourPayResult,
  TourPayout,
  TourQuoteResult,
  TourRefundResult,
  TourReserveInput,
  TourReserveResult,
  TourReview,
  TourReviewInput,
  TourTripLocation,
  TourTripOperatorAction,
  TourTripRun,
  TourTripSnapshot,
  TourSplitInfo,
  TourSplitPayInput,
  TourSplitResult,
} from '../types/tours.types'

type ServerErrorBody = { error?: string; code?: string }

function rewriteCoverUrl<T extends { coverImageUrl?: string | null }>(row: T): T {
  if (!row.coverImageUrl) return row
  return {
    ...row,
    coverImageUrl: rewriteAndroidEmulatorLoopback(row.coverImageUrl, {
      os: Platform.OS,
      isPhysicalDevice: Device.isDevice,
      assetHost: androidEmulatorAssetHost(Platform.OS, Device.isDevice),
      metroOrigin:
        metroOriginFromScriptURL(NativeModules.SourceCode?.scriptURL) ?? 'http://10.0.2.2:8081',
    }),
  }
}

function toToursPayWire(input: {
  payer: CheckoutPayer
  card?: CardPaymentData
  savedProviderToken?: string
  savedCardCvv?: string
  provider?: 'wompi' | 'paypal'
}) {
  return {
    payer: payerToCreatePaymentWire(input.payer),
    ...(input.provider ? { provider: input.provider } : {}),
    ...(input.card ? { card: cardToCreatePaymentWire(input.card) } : {}),
    ...(input.savedProviderToken ? { savedProviderToken: input.savedProviderToken } : {}),
    ...(input.savedCardCvv ? { savedCardCvv: input.savedCardCvv } : {}),
  }
}

/** Error de una edge function con `code` propio (p. ej. TOURS_CAPACITY_EXCEEDED). */
function edgeFunctionFailure(error: unknown): ServiceError {
  const functionsError = error as {
    status?: number
    statusCode?: number
    context?: unknown
    message?: string
  }
  const body = functionsError.context as ServerErrorBody | undefined
  if (body && typeof body.code === 'string') {
    return new ServiceError({
      message: body.error ?? body.code,
      code: body.code,
      statusCode: functionsError.statusCode ?? functionsError.status ?? 422,
    })
  }
  return ServiceError.fromSupabaseError(
    error as { message: string; code?: string; status?: number },
  )
}

function previewFixtureOnlyError(operation: string): ServiceError {
  return new ServiceError({
    message: `${operation} is available only for a fixture booking in isolated Preview`,
    code: 'PREVIEW_FIXTURE_ONLY',
    statusCode: 403,
  })
}

function previewMoneyPath(identifier: string): 'fixture' | 'production' {
  const path = resolvePreviewMoneyPath({
    isPreviewRuntime: isIsolatedPreviewBuild,
    identifier,
  })
  if (path === 'blocked') throw previewFixtureOnlyError('This payment operation')
  return path
}

function mapHost(row: Record<string, unknown>): TourHost {
  return {
    id: String(row.id),
    displayName: String(row.display_name ?? ''),
    businessName: (row.business_name as string) ?? null,
    rntRegistrationNumber: (row.rnt_registration_number as string) ?? null,
    vmtTourismPermitNumber: (row.vmt_tourism_permit_number as string) ?? null,
    insurancePolicyNumber: (row.insurance_policy_number as string) ?? null,
    insuranceExpiresAt: (row.insurance_expires_at as string) ?? null,
    verificationStatus: (row.verification_status as TourHost['verificationStatus']) ?? 'pending',
    isOperational: Boolean(row.is_operational),
  }
}

function mapHostUnit(row: Record<string, unknown>): TourHostUnit {
  return {
    id: String(row.id),
    label: String(row.label ?? ''),
    makeModel: (row.make_model as string) ?? null,
    licensePlate: String(row.license_plate ?? ''),
    seats: Number(row.seats ?? 0),
    cargoVolumeM3: Number(row.cargo_volume_m3 ?? 0),
    status: (row.status as TourHostUnit['status']) ?? 'active',
  }
}

function mapHostDeparture(row: Record<string, unknown>): TourHostDeparture {
  return {
    id: String(row.id),
    experienceId: String(row.experience_id),
    startsAt: String(row.starts_at),
    endsAt: String(row.ends_at),
    seatsCapacity: Number(row.seats_capacity ?? 0),
    cargoVolumeM3: Number(row.cargo_volume_m3 ?? 0),
    status: (row.status as TourHostDeparture['status']) ?? 'open',
  }
}

function mapHostExperience(row: Record<string, unknown>): TourHostExperience {
  return {
    id: String(row.id),
    title: String(row.title ?? ''),
    durationMinutes: Number(row.duration_minutes ?? 0),
  }
}

function mapPayout(row: Record<string, unknown>): TourPayout {
  return {
    id: String(row.id),
    amountCents: Number(row.amount_cents ?? 0),
    currency: String(row.currency ?? 'USD'),
    status: (row.status as TourPayout['status']) ?? 'pending',
    providerReference: (row.provider_reference as string) ?? null,
    createdAt: String(row.created_at ?? ''),
    paidAt: (row.paid_at as string) ?? null,
  }
}

function mapTripRun(value: unknown): TourTripRun | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const row = value as Record<string, unknown>
  return {
    id: String(row.id),
    departureId: String(row.departure_id ?? row.departureId ?? ''),
    driverId: (row.driver_id ?? row.driverId ?? null) as string | null,
    status: (row.status as TourTripRun['status']) ?? 'scheduled',
    currentStopPosition: Number(row.current_stop_position ?? row.currentStopPosition ?? 0),
    etaAt: (row.eta_at ?? row.etaAt ?? null) as string | null,
    emergencyStatus:
      (row.emergency_status as TourTripRun['emergencyStatus']) ??
      (row.emergencyStatus as TourTripRun['emergencyStatus']) ??
      'none',
    lastLocationAt: (row.last_location_at ?? row.lastLocationAt ?? null) as string | null,
    startedAt: (row.started_at ?? row.startedAt ?? null) as string | null,
    arrivedAt: (row.arrived_at ?? row.arrivedAt ?? null) as string | null,
    completedAt: (row.completed_at ?? row.completedAt ?? null) as string | null,
    updatedAt: String(row.updated_at ?? row.updatedAt ?? ''),
  }
}

function mapTripLocation(value: unknown): TourTripLocation | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const row = value as Record<string, unknown>
  return {
    latitude: Number(row.latitude),
    longitude: Number(row.longitude),
    accuracyM:
      row.accuracy_m == null && row.accuracyM == null
        ? null
        : Number(row.accuracy_m ?? row.accuracyM),
    headingDeg:
      row.heading_deg == null && row.headingDeg == null
        ? null
        : Number(row.heading_deg ?? row.headingDeg),
    speedMps:
      row.speed_mps == null && row.speedMps == null ? null : Number(row.speed_mps ?? row.speedMps),
    capturedAt: String(row.captured_at ?? row.capturedAt ?? ''),
  }
}

function mapStops(value: unknown): TourTripSnapshot['stops'] {
  if (!Array.isArray(value)) return []
  return value.map((raw) => {
    const row = raw as Record<string, unknown>
    return {
      id: String(row.id),
      position: Number(row.position),
      label: String(row.label ?? ''),
      latitude: Number(row.latitude),
      longitude: Number(row.longitude),
      kind: (row.kind as TourTripSnapshot['stops'][number]['kind']) ?? 'waypoint',
    }
  })
}

function mapTripSnapshot(raw: Record<string, unknown>): TourTripSnapshot {
  const booking = (raw.booking ?? {}) as Record<string, unknown>
  const departure = (raw.departure ?? {}) as Record<string, unknown>
  const experience = (raw.experience ?? {}) as Record<string, unknown>
  const guardian = (raw.guardian ?? {}) as Record<string, unknown>
  const driver = raw.driver as Record<string, unknown> | null | undefined
  const operator = raw.operator as Record<string, unknown> | null | undefined
  const availableDrivers = Array.isArray(operator?.availableDrivers)
    ? operator.availableDrivers
    : []
  const activeShare = guardian.activeShare as Record<string, unknown> | null | undefined
  return {
    viewerRole: (raw.viewerRole as TourTripSnapshot['viewerRole']) ?? 'participant',
    booking: {
      id: String(booking.id ?? ''),
      status: booking.status as TourBookingStatus,
      passengerCount: Number(booking.passengerCount ?? 0),
    },
    departure: {
      id: String(departure.id ?? ''),
      startsAt: String(departure.startsAt ?? ''),
      endsAt: String(departure.endsAt ?? ''),
    },
    experience: {
      id: String(experience.id ?? ''),
      title: String(experience.title ?? ''),
      originLabel: String(experience.originLabel ?? ''),
      destinationLabel: String(experience.destinationLabel ?? ''),
      routeGeojson: experience.routeGeojson ?? null,
    },
    stops: mapStops(raw.stops),
    run: mapTripRun(raw.run),
    latestLocation: mapTripLocation(raw.latestLocation),
    driver: driver
      ? {
          displayName: String(driver.displayName ?? ''),
          avatarUrl: (driver.avatarUrl as string) ?? null,
          verified: Boolean(driver.verified),
        }
      : null,
    guardian: {
      configured: Boolean(guardian.configured),
      includesMinors: Boolean(guardian.includesMinors),
      responsibleAdultName: (guardian.responsibleAdultName as string) ?? null,
      consentedAt: (guardian.consentedAt as string) ?? null,
      activeShare: activeShare
        ? {
            guardianName: String(activeShare.guardianName ?? ''),
            expiresAt: String(activeShare.expiresAt ?? ''),
            createdAt: String(activeShare.createdAt ?? ''),
          }
        : null,
    },
    operator: operator
      ? {
          canAssignDriver: Boolean(operator.canAssignDriver),
          availableDrivers: availableDrivers.map((value) => {
            const item = value as Record<string, unknown>
            return {
              id: String(item.id ?? ''),
              displayName: String(item.displayName ?? ''),
              avatarUrl: (item.avatarUrl as string) ?? null,
              verified: Boolean(item.verified),
              operatorRole: item.operatorRole === 'admin' ? 'admin' : 'driver',
              licenseExpiresAt: String(item.licenseExpiresAt ?? ''),
            }
          }),
        }
      : null,
  }
}

function mapGuardianSnapshot(raw: Record<string, unknown>): TourGuardianSnapshot {
  const trip = mapTripSnapshot({ ...raw, viewerRole: 'participant', guardian: {} })
  return {
    guardianName: String(raw.guardianName ?? ''),
    expiresAt: String(raw.expiresAt ?? ''),
    booking: trip.booking,
    departure: trip.departure,
    experience: trip.experience,
    stops: trip.stops,
    run: trip.run,
    latestLocation: trip.latestLocation,
    driver: trip.driver,
  }
}

class ToursServiceImpl {
  async getCatalog(country: string): Promise<TourCatalogExperience[]> {
    const { data, error } = await supabase.functions.invoke<{
      experiences?: TourCatalogExperience[]
      error?: string
    }>('tours-catalog', { body: { country } })

    if (error) throw edgeFunctionFailure(error)
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw new ServiceError({
        message: 'tours-catalog returned malformed data',
        code: 'TOURS_CATALOG_FAILED',
        statusCode: 422,
      })
    }
    return (data.experiences ?? []).map(rewriteCoverUrl)
  }

  async getExperience(experienceId: string): Promise<TourExperienceDetail> {
    const { data, error } = await supabase.functions.invoke<
      TourExperienceDetail & { error?: string }
    >('tours-experience', { body: { experienceId } })

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Experience request failed',
        code:
          data && 'code' in data && typeof data.code === 'string'
            ? data.code
            : 'TOURS_EXPERIENCE_FAILED',
        statusCode: 422,
      })
    }

    return rewriteCoverUrl(data)
  }

  async quote(input: CreateTourBookingInput): Promise<TourQuoteResult> {
    const { data, error } = await supabase.functions.invoke<
      TourQuoteResult & { error?: string; code?: string }
    >('tours-quote', { body: input })

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Quote request failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_QUOTE_FAILED',
        statusCode: 422,
      })
    }

    return data
  }

  /**
   * The server validates SV-only routing, runs the calibrated model and creates
   * a private temporary experience. No price supplied by the handset is used.
   */
  async createCustomQuote(input: TourCustomQuoteInput): Promise<TourCustomQuoteResult> {
    if (isIsolatedPreviewBuild) throw previewFixtureOnlyError('Custom quote creation')
    const { data, error } = await supabase.functions.invoke<
      TourCustomQuoteResult & { error?: string; code?: string }
    >('tours-custom-quote', { body: input })

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Custom quote request failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_CUSTOM_QUOTE_FAILED',
        statusCode: 422,
      })
    }
    return data
  }

  /** Aparta capacidad y crea la reserva (F2). No cobra. */
  async reserve(input: TourReserveInput): Promise<TourReserveResult> {
    if (isIsolatedPreviewBuild) throw previewFixtureOnlyError('Tour reservation')
    const { data, error } = await supabase.functions.invoke<
      TourReserveResult & { error?: string; code?: string }
    >('tours-reserve', { body: input })

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Reserve request failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_RESERVE_FAILED',
        statusCode: 422,
      })
    }

    return data
  }

  /** Crea el cobro por el puerto de pasarelas (F2). */
  async pay(input: TourPayInput): Promise<TourPayResult> {
    if (previewMoneyPath(input.bookingId) === 'fixture') {
      return previewPayTour(input)
    }
    const { data, error } = await supabase.functions.invoke<
      Record<string, unknown> & { error?: string; code?: string }
    >('tours-pay', {
      body: {
        bookingId: input.bookingId,
        checkoutOrigin: optionalClientCheckoutOrigin(),
        ...toToursPayWire(input),
      },
    })

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Payment request failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_PAYMENT_FAILED',
        statusCode: 422,
      })
    }

    return chargeIntentFromCreatePayment(data)
  }

  /** Confirma un cobro después de 3DS / redirect (F2). */
  async confirm(bookingId: string, transactionId: string): Promise<{ status: string }> {
    if (previewMoneyPath(bookingId) === 'fixture') {
      return { status: 'captured' }
    }
    if (transactionId.startsWith('PAYPAL-PREVIEW') || transactionId.startsWith('WOMPI-PREVIEW')) {
      throw previewFixtureOnlyError('Preview payment confirmation')
    }
    const { data, error } = await supabase.functions.invoke<{
      status: string
      error?: string
      code?: string
    }>('tours-confirm', { body: { bookingId, transactionId } })

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Confirm request failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_CONFIRM_FAILED',
        statusCode: 422,
      })
    }

    return data
  }

  /** Reservas del usuario (F2). */
  async listBookings(): Promise<TourBookingSummary[]> {
    const { data, error } = await supabase.functions.invoke<{
      bookings?: TourBookingSummary[]
      error?: string
      code?: string
    }>('tours-bookings', {})

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Bookings request failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_BOOKINGS_FAILED',
        statusCode: 422,
      })
    }

    return data.bookings ?? []
  }

  /** Cancela una reserva pagada y devuelve lo que corresponda (F2). */
  async refundBooking(bookingId: string): Promise<TourRefundResult> {
    const { data, error } = await supabase.functions.invoke<
      TourRefundResult & { error?: string; code?: string }
    >('tours-refund', { body: { bookingId } })

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Refund request failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_REFUND_FAILED',
        statusCode: 422,
      })
    }

    return data
  }

  /** Divide una reserva en partes (F3). */
  async createSplit(bookingId: string, participantCount: number): Promise<TourSplitResult> {
    if (previewMoneyPath(bookingId) === 'fixture') {
      const existing = peekPreviewSplit(bookingId)
      if (existing) return existing
      const previewTotal = useToursCheckoutStore.getState().context?.booking.totalCents ?? 20000
      return previewCreateSplit(bookingId, participantCount, previewTotal, {
        title: 'Ruta de las Flores',
        originLabel: 'San Salvador',
        destinationLabel: 'Apaneca · Ataco · Juayúa',
        startsAt: new Date(Date.now() + 10 * 24 * 3600 * 1000).toISOString(),
      })
    }
    const { data, error } = await supabase.functions.invoke<
      TourSplitResult & { error?: string; code?: string }
    >('tours-split', {
      body: {
        bookingId,
        participantCount,
        checkoutOrigin: optionalClientCheckoutOrigin(),
      },
    })

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Split request failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_SPLIT_FAILED',
        statusCode: 422,
      })
    }

    return data
  }

  /** Info pública de una parte (F3). */
  async getSplitInfo(token: string): Promise<TourSplitInfo> {
    if (previewMoneyPath(token) === 'fixture') {
      const info = previewSplitInfo(token)
      if (!info) {
        throw new ServiceError({
          message: 'Split not found',
          code: 'TOURS_SPLIT_NOT_FOUND',
          statusCode: 404,
        })
      }
      return info
    }
    const { data, error } = await supabase.functions.invoke<
      TourSplitInfo & { error?: string; code?: string }
    >('tours-split-info', { body: { token } })

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Split info failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_SPLIT_NOT_FOUND',
        statusCode: 404,
      })
    }

    return data
  }

  /** Paga una parte del split (F3). */
  async paySplit(input: TourSplitPayInput): Promise<TourPayResult> {
    if (previewMoneyPath(input.token) === 'fixture') {
      return previewPaySplit(input)
    }
    const { data, error } = await supabase.functions.invoke<
      Record<string, unknown> & { error?: string; code?: string }
    >('tours-split-pay', {
      body: {
        token: input.token,
        checkoutOrigin: optionalClientCheckoutOrigin(),
        ...toToursPayWire(input),
      },
    })

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Split payment failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_PAYMENT_FAILED',
        statusCode: 422,
      })
    }

    return chargeIntentFromCreatePayment(data)
  }

  /** Onboarding: perfil del host + unidades (F3). */
  async getHost(): Promise<{ host: TourHost | null; units: TourHostUnit[] }> {
    const { data, error } = await supabase.functions.invoke<{
      host: Record<string, unknown> | null
      units: Record<string, unknown>[]
      error?: string
      code?: string
    }>('tours-host', {})

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Host request failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_HOST_FAILED',
        statusCode: 422,
      })
    }
    return {
      host: data.host ? mapHost(data.host) : null,
      units: (data.units ?? []).map(mapHostUnit),
    }
  }

  /** Alta/edición del perfil del host (F3). */
  async saveHost(input: TourHostProfileInput): Promise<TourHost> {
    const { data, error } = await supabase.functions.invoke<{
      host?: Record<string, unknown>
      error?: string
      code?: string
    }>('tours-host', { body: input })

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error || !data.host) {
      throw new ServiceError({
        message: data?.error ?? 'Host save failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_HOST_FAILED',
        statusCode: 422,
      })
    }
    return mapHost(data.host)
  }

  /** Alta/edición de una unidad (F3). */
  async saveHostUnit(input: TourHostUnitInput): Promise<TourHostUnit> {
    const { data, error } = await supabase.functions.invoke<{
      unit?: Record<string, unknown>
      error?: string
      code?: string
    }>('tours-host-units', { body: input })

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error || !data.unit) {
      throw new ServiceError({
        message: data?.error ?? 'Unit save failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_HOST_FAILED',
        statusCode: 422,
      })
    }
    return mapHostUnit(data.unit)
  }

  /** Ganancias y payouts del host (F3). */
  async getHostPayouts(): Promise<{ balance: TourHostBalance; payouts: TourPayout[] }> {
    const { data, error } = await supabase.functions.invoke<{
      balance?: Record<string, unknown>
      payouts?: Record<string, unknown>[]
      error?: string
      code?: string
    }>('tours-host-payouts', {})

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Payouts request failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_HOST_FAILED',
        statusCode: 422,
      })
    }
    const balance = data.balance ?? {}
    return {
      balance: {
        earnedCents: Number(balance.earnedCents ?? 0),
        disbursedCents: Number(balance.disbursedCents ?? 0),
        availableCents: Number(balance.availableCents ?? 0),
      },
      payouts: (data.payouts ?? []).map(mapPayout),
    }
  }

  /** Reseñas de una experiencia (F4). */
  async listReviews(experienceId: string): Promise<TourReview[]> {
    const { data, error } = await supabase.functions.invoke<{
      reviews?: TourReview[]
      error?: string
      code?: string
    }>('tours-reviews', { body: { experienceId } })

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Reviews request failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_REVIEWS_FAILED',
        statusCode: 422,
      })
    }
    return data.reviews ?? []
  }

  /** Deja una reseña (F4). */
  async submitTourReview(input: TourReviewInput): Promise<TourReview> {
    const { data, error } = await supabase.functions.invoke<{
      review?: TourReview
      error?: string
      code?: string
    }>('tours-reviews', { body: input })

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error || !data.review) {
      throw new ServiceError({
        message: data?.error ?? 'Review save failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_REVIEWS_FAILED',
        statusCode: 422,
      })
    }
    return data.review
  }

  /** Experiencias + salidas del host (F3). */
  async getHostDepartures(): Promise<TourHostDeparturesResult> {
    const { data, error } = await supabase.functions.invoke<{
      experiences?: Record<string, unknown>[]
      departures?: Record<string, unknown>[]
      error?: string
      code?: string
    }>('tours-host-departures', {})

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Host departures request failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_HOST_FAILED',
        statusCode: 422,
      })
    }
    return {
      experiences: (data.experiences ?? []).map(mapHostExperience),
      departures: (data.departures ?? []).map(mapHostDeparture),
    }
  }

  /** Crea una salida para una experiencia del host (F3). */
  async createHostDeparture(input: TourHostDepartureInput): Promise<TourHostDeparture> {
    const { data, error } = await supabase.functions.invoke<{
      departure?: Record<string, unknown>
      error?: string
      code?: string
    }>('tours-host-departures', { body: input })

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error || !data.departure) {
      throw new ServiceError({
        message: data?.error ?? 'Departure save failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_HOST_FAILED',
        statusCode: 422,
      })
    }
    return mapHostDeparture(data.departure)
  }

  /** Reservas recibidas por el host (F3). */
  async getHostBookings(): Promise<TourHostBooking[]> {
    const { data, error } = await supabase.functions.invoke<{
      bookings?: TourHostBooking[]
      error?: string
      code?: string
    }>('tours-host-bookings', {})

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Host bookings request failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_HOST_FAILED',
        statusCode: 422,
      })
    }
    return data.bookings ?? []
  }

  /** Avanza el estado de una reserva (start|complete) (F3). */
  async advanceHostBooking(
    bookingId: string,
    action: 'start' | 'complete',
  ): Promise<{ id: string; status: TourBookingStatus }> {
    const { data, error } = await supabase.functions.invoke<{
      booking?: { id: string; status: TourBookingStatus }
      error?: string
      code?: string
    }>('tours-host-bookings', { body: { bookingId, action } })

    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error || !data.booking) {
      throw new ServiceError({
        message: data?.error ?? 'Booking update failed',
        code: typeof data?.code === 'string' ? data.code : 'TOURS_HOST_FAILED',
        statusCode: 422,
      })
    }
    return data.booking
  }

  /** Estado operacional completo, con ruta, parada actual y ubicación reciente. */
  async getTrip(bookingId: string): Promise<TourTripSnapshot> {
    const { data, error } = await supabase.functions.invoke<Record<string, unknown>>('tours-trip', {
      body: { bookingId },
    })
    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: String(data?.error ?? 'Trip request failed'),
        code: String(data?.code ?? 'TOURS_TRIP_FAILED'),
        statusCode: 422,
      })
    }
    return mapTripSnapshot(data)
  }

  async runTripAction(
    bookingId: string,
    action: TourTripOperatorAction | 'trigger_emergency' | 'advance_stop' | 'update_location',
    payload: Record<string, unknown> = {},
  ): Promise<void> {
    const { data, error } = await supabase.functions.invoke<Record<string, unknown>>('tours-trip', {
      body: { bookingId, action, ...payload },
    })
    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: String(data?.error ?? 'Trip update failed'),
        code: String(data?.code ?? 'TOURS_TRIP_FAILED'),
        statusCode: 422,
      })
    }
  }

  async createGuardianShare(input: TourGuardianShareInput): Promise<TourGuardianShareResult> {
    const { data, error } = await supabase.functions.invoke<
      TourGuardianShareResult & { error?: string; code?: string }
    >('tours-trip', { body: { ...input, action: 'create_guardian_share' } })
    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error || !data.shareUrl) {
      throw new ServiceError({
        message: data?.error ?? 'Guardian share failed',
        code: data?.code ?? 'TOURS_GUARDIAN_FAILED',
        statusCode: 422,
      })
    }
    return data
  }

  async revokeGuardianShare(bookingId: string): Promise<void> {
    const { data, error } = await supabase.functions.invoke<Record<string, unknown>>('tours-trip', {
      body: { bookingId, action: 'revoke_guardian_share' },
    })
    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: String(data?.error ?? 'Guardian revoke failed'),
        code: String(data?.code ?? 'TOURS_GUARDIAN_FAILED'),
        statusCode: 422,
      })
    }
  }

  async registerLiveActivity(
    bookingId: string,
    activityId: string,
    pushToken: string,
  ): Promise<void> {
    const { data, error } = await supabase.functions.invoke<Record<string, unknown>>('tours-trip', {
      body: { bookingId, action: 'register_live_activity', activityId, pushToken },
    })
    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: String(data?.error ?? 'Live Activity registration failed'),
        code: String(data?.code ?? 'TOURS_LIVE_ACTIVITY_FAILED'),
        statusCode: 422,
      })
    }
  }

  async getGuardianTrip(token: string): Promise<TourGuardianSnapshot> {
    const { data, error } = await supabase.functions.invoke<Record<string, unknown>>(
      'tours-guardian',
      { body: { token } },
    )
    if (error) throw edgeFunctionFailure(error)
    if (!data || data.error) {
      throw new ServiceError({
        message: String(data?.error ?? 'Guardian link failed'),
        code: String(data?.code ?? 'TOURS_GUARDIAN_NOT_FOUND'),
        statusCode: 404,
      })
    }
    return mapGuardianSnapshot(data)
  }
}

export const toursService = new ToursServiceImpl()
